/** Pure billing / trial helpers shared by API routes and Vitest. */

import { canonicalizePlanCode, STARTER_PLAN_CODE } from './planCatalogCode.js';

export type BillingPlanStripeRow = {
  stripe_price_id?: string | null;
  stripe_price_id_annual?: string | null;
  monthly_price_usd_cents?: number | null;
  annual_price_usd_cents?: number | null;
};

export function parseAppConfigBoolean(raw: unknown): boolean {
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw ?? '')
    .replace(/^"|"$/g, '')
    .trim()
    .toLowerCase();
  return s === 'true' || s === '1';
}

export function parseAppConfigString(raw: unknown, fallback = ''): string {
  if (raw == null) return fallback;
  return String(raw).replace(/^"|"$/g, '');
}

export function parseAppConfigNumber(raw: unknown, fallback: number): number {
  const n = Number(String(raw ?? '').replace(/^"|"$/g, ''));
  return Number.isFinite(n) ? n : fallback;
}

/** Paid Starter is the default self-serve catalog key. */
export const DEFAULT_SELF_SERVE_PLAN_CODES = [STARTER_PLAN_CODE] as const;

export function parseSelfServePlanCodes(
  raw: unknown,
  fallback: readonly string[] = DEFAULT_SELF_SERVE_PLAN_CODES,
): string[] {
  if (raw == null) return [...fallback];
  if (Array.isArray(raw)) {
    return raw
      .map((row) => canonicalizePlanCode(String(row ?? '').replace(/^"|"$/g, '')))
      .filter((row) => row.length > 0);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.replace(/^"|"$/g, '').trim();
    if (!trimmed) return [...fallback];
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) return parseSelfServePlanCodes(parsed, fallback);
    } catch {
      /* comma / space separated */
    }
    return trimmed
      .split(/[, \n]+/)
      .map((row) => canonicalizePlanCode(row))
      .filter(Boolean);
  }
  return [...fallback];
}

/** Annual list price from monthly cents and discount percent (e.g. 10 → 10% off). */
export function annualPriceFromMonthly(monthlyCents: number, discountPercent: number): number {
  const discount = Math.min(100, Math.max(0, discountPercent));
  return Math.round(monthlyCents * 12 * (1 - discount / 100));
}

export function isStripeCheckoutAvailable(
  provider: string,
  stripeKeyConfigured: boolean,
  plans: BillingPlanStripeRow[],
): boolean {
  if (!stripeKeyConfigured || provider !== 'stripe') return false;
  return plans.some(
    (p) =>
      p.monthly_price_usd_cents != null &&
      (Boolean(p.stripe_price_id?.trim()) || Boolean(p.stripe_price_id_annual?.trim())),
  );
}

export function shouldRequireCardAtSignup(trialRequiresCard: boolean, checkoutAvailable: boolean): boolean {
  return trialRequiresCard && checkoutAvailable;
}

/** Mirrors `expire_overdue_trials()` grace logic for QA matrices. */
export function shouldMarkTrialPaused(
  orgStatus: string,
  trialEndsAt: string | null,
  graceDays: number,
  nowMs: number = Date.now(),
): boolean {
  return shouldMarkTrialPastDue(orgStatus, trialEndsAt, graceDays, nowMs);
}

/** @deprecated Prefer shouldMarkTrialPaused — expired no-card trials pause, they are not past_due. */
export function shouldMarkTrialPastDue(
  orgStatus: string,
  trialEndsAt: string | null,
  graceDays: number,
  nowMs: number = Date.now(),
): boolean {
  if (orgStatus !== 'trial' || !trialEndsAt) return false;
  const grace = Number.isFinite(graceDays) ? graceDays : 7;
  const endMs = new Date(trialEndsAt).getTime();
  if (Number.isNaN(endMs)) return false;
  const graceMs = grace * 86_400_000;
  return endMs + graceMs < nowMs;
}

export function mapStripeSubscriptionStatus(stripeStatus: string): string {
  switch (stripeStatus) {
    case 'trialing':
      return 'trialing';
    case 'active':
      return 'active';
    case 'past_due':
    case 'unpaid':
      return 'past_due';
    case 'paused':
      return 'paused';
    case 'canceled':
    case 'incomplete_expired':
      return 'cancelled';
    default:
      return 'past_due';
  }
}

export const STRIPE_GO_LIVE_STEPS = [
  'Complete Stripe business verification and obtain live API keys',
  'Create Products / Prices in Stripe Dashboard for each plan (monthly recurring)',
  'Set stripe_price_id on each plan in Platform Admin → Plans',
  'Add STRIPE_SECRET_KEY and STRIPE_WEBHOOK_SECRET to the serviceapp.ai Vercel project',
  'Register webhook URL: https://serviceapp.ai/api/saas/stripe-webhook',
  'Set saas_billing_provider to stripe in Platform Admin',
  'Set monthly and annual stripe_price_id fields (annual = 10% off by default)',
  'Add https://serviceapp.ai/** to Supabase Auth redirect URLs',
] as const;
